import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomerService from "../../../../services/CustomerService";
import LocationInput from "../../Organisation/locationInput";
import { FaUser, FaMapMarkerAlt } from "react-icons/fa";
import { colors } from "../../../config/colors";

const CreateCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [locationSelected, setLocationSelected] = useState(false);
  
  // Add draft-related state
  const DRAFT_STORAGE_KEY = "customer_draft_data";
  const [showDraftModal, setShowDraftModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    street: "",
    city: "",
  });

  // Add draft functionality methods
  const saveDraftToLocalStorage = () => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
        formData,
        timestamp: new Date().toISOString()
      }));
      toast.success("Draft saved successfully");
      navigate("/customer-list");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    }
  };

  const loadDraftFromLocalStorage = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftData) {
        const parsedData = JSON.parse(draftData);
        setFormData(parsedData.formData);
        
        // Clear the draft after loading
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        toast.success("Draft loaded successfully");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error loading draft:", error);
      toast.error("Failed to load draft");
      return false;
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  };

  // Add handlers for the modal actions
  const handleLoadDraft = () => {
    loadDraftFromLocalStorage();
    setShowDraftModal(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftModal(false);
  };
  // Add validation message state
  const [validationMessage, setValidationMessage] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    street: ''
  });

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      const fetchCustomerData = async () => {
        setLoading(true);
        try {
          const response = await CustomerService.getCustomerById(id);
          if (response.data.success) {
            setFormData(response.data.data);
          }
        } catch (error) {
          console.error("Error fetching customer data:", error);
          toast.error("Failed to fetch customer data");
        } finally {
          setLoading(false);
        }
      };
      fetchCustomerData();
    } else {
      // Only check for drafts in create mode, not edit mode
      const draftExists = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftExists) {
        setShowDraftModal(true);
      }
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      setIsEditMode(false);
      setFormData({
        name: "",
        email: "",
        phoneNumber: "",
        street: "",
        city: "",
      });
      // Optionally reset validation messages or other state here
    }
  }, [id]);

  const showTemporaryError = (field, message) => {
    setValidationMessage(prev => ({
      ...prev,
      [field]: message
    }));

    // Clear the message after 1 second
    setTimeout(() => {
      setValidationMessage(prev => ({
        ...prev,
        [field]: ''
      }));
    }, 2000);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'name' || name === 'street') {
      // Prevent starting with space
      if (value === ' ') {
        showTemporaryError(name, 'Cannot start with space');
        return;
      }
      // Only allow letters, numbers, spaces and basic punctuation
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s.,'-]/g, '');
      if (sanitizedValue !== value) {
        showTemporaryError(name, 'Special characters not allowed');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else if (name === 'email') {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        showTemporaryError('email', 'Invalid email format');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name === 'phoneNumber') {
      // Only allow numbers and limit to 10 digits
      const sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
      if (value !== sanitizedValue) {
        showTemporaryError('phoneNumber', 'Only numbers allowed (max 10 digits)');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleLocationChange = (location) => {
    setFormData((prev) => ({
      ...prev,
      city: location,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isEditMode) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          street: formData.street,
          city: formData.city,
        };
        response = await CustomerService.updateCustomer(id, updateData);
      } else {
        response = await CustomerService.createCustomer(formData);
      }

      if (response.data.success) {
        toast.success(
          `Customer ${isEditMode ? "updated" : "created"} successfully`
        );
        // Clear any draft if we successfully created a customer
        if (!isEditMode) {
          clearDraft();
        }
        navigate("/customer-list");
      } else {
        toast.error(`Failed to ${isEditMode ? "update" : "create"} customer`);
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} customer:`,
        error
      );
      toast.error(
        error.response?.data?.message || 
        `Failed to ${isEditMode ? "update" : "create"} customer`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/customer-list");
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 pb-4">
      {/* Floating Header - Solid white background */}
      <div className="sticky top-0 bg-white border-b border-accent-light z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-accent-light/20 transition-colors"
              >
                <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-primary-headings">
                {isEditMode ? "Edit Customer" : "Create Customer"}
              </h1>
            </div>
            
            {/* Add top action buttons for draft functionality */}
            {/* {!isEditMode && (
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={saveDraftToLocalStorage}
                  className="px-6 py-2.5 border border-accent-light/30 rounded-lg text-accent-dark hover:bg-accent-light/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light transition-colors duration-200"
                >
                  Save Draft & Close
                </button>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="w-16 h-16 relative">
              <div className="w-16 h-16 rounded-full border-4 border-accent-light"></div>
              <div className="w-16 h-16 rounded-full border-4 border-primary-headings border-t-transparent animate-spin absolute top-0"></div>
            </div>
            <p className="mt-4 text-accent-dark">
              {isEditMode ? "Updating customer..." : "Creating customer..."}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <FaUser className="w-5 h-5 text-primary-headings" />
                  <h2 className="text-lg font-semibold text-primary-headings">Basic Information</h2>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      Name <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter customer name"
                      />
                      {validationMessage.name && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      Email
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter email address"
                      />
                      {validationMessage.email && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      Phone
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter phone number"
                      />
                      {validationMessage.phoneNumber && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.phoneNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Address Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="w-5 h-5 text-primary-headings" />
                  <h2 className="text-lg font-semibold text-primary-headings">Address Information</h2>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      Street
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter street address"
                      />
                      {validationMessage.street && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.street}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-primary-headings">
                      City
                    </label>
                    <LocationInput
                      setUserLocation={handleLocationChange}
                      setLocationSelected={setLocationSelected}
                      location={formData.city}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 border-t border-accent-light/20 pt-6">
              <div className="flex justify-end items-center">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-cta text-white rounded-lg hover:bg-cta/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta transition-colors duration-200"
                  style={{ backgroundColor: colors.highlights.callToAction }}
                >
                  {isEditMode ? "Update Customer" : "Create Customer"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Draft Modal */}
      {showDraftModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>
            
            {/* Modal panel */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
                    <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Draft Found
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      A draft customer was found. Would you like to load it?
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  No, Discard
                </button>
                <button
                  type="button"
                  onClick={handleLoadDraft}
                  className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Yes, Load Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCustomer;
